<template>
  <section v-show="active" class="app-modal__section">
    <header class="app-modal__section-head">
      <h4>{{ saleQuoteText.header.title }}</h4>
      <p>{{ saleQuoteText.header.description }}</p>
    </header>

    <div class="app-modal__grid two-columns">
      <div class="app-modal-field full-width">
        <span>{{ saleQuoteText.fields.quoteType }}</span>
        <multiselect
          v-model="form.quoteType"
          :options="options.quoteTypes"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          :max-height="160"
          :placeholder="saleQuoteText.placeholders.quoteType"
          class="app-modal-select"
        >
          <template #noResult>
            <span>{{ salesModalText.common.noOption }}</span>
          </template>
          <template #noOptions>
            <span>{{ salesModalText.common.noOption }}</span>
          </template>
        </multiselect>
      </div>

      <div class="app-modal-field full-width">
        <span>{{ saleQuoteText.fields.format }}</span>
        <div class="sale-quote-mode">
          <label class="app-modal-toggle">
            <input
              type="checkbox"
              :checked="form.quoteMode === 'url'"
              @change="$emit('change-quote-mode', 'url')"
            />
            <span>{{ saleQuoteText.options.url }}</span>
          </label>

          <label class="app-modal-toggle">
            <input
              type="checkbox"
              :checked="form.quoteMode === 'file'"
              @change="$emit('change-quote-mode', 'file')"
            />
            <span>{{ saleQuoteText.options.file }}</span>
          </label>
        </div>
      </div>

      <div class="app-modal-field full-width">
        <span>{{ saleQuoteText.fields.quote }}</span>
        <input
          v-if="form.quoteMode === 'url'"
          v-model.trim="form.quoteUrl"
          type="url"
          class="form-control"
          :placeholder="saleQuoteText.placeholders.quoteUrl"
        />

        <div v-else class="sale-file-upload">
          <input
            ref="quoteFileInput"
            type="file"
            class="sale-file-upload__input"
            @change="onQuoteFileChange"
          />
          <button
            type="button"
            class="app-header-btn app-header-btn--secondary sale-file-upload__trigger"
            @click="openQuoteFilePicker"
          >
            {{ saleQuoteText.file.select }}
          </button>
          <span class="sale-file-upload__name">
            {{ form.quoteFile ? form.quoteFile.name : saleQuoteText.file.none }}
          </span>
        </div>
      </div>

      <div class="app-modal-field">
        <span>{{ saleQuoteText.fields.quotePrincipal }}</span>
        <multiselect
          v-model="form.quotePrincipal"
          :options="options.quoteCatalogs"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          :max-height="160"
          :placeholder="saleQuoteText.placeholders.quotePrincipal"
          class="app-modal-select"
        >
          <template #noResult>
            <span>{{ salesModalText.common.noOption }}</span>
          </template>
          <template #noOptions>
            <span>{{ salesModalText.common.noOption }}</span>
          </template>
        </multiselect>
      </div>

      <div class="app-modal-field">
        <span>{{ saleQuoteText.fields.quoteExtras }}</span>
        <multiselect
          v-model="form.quoteExtras"
          :options="options.quoteCatalogs"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          :max-height="160"
          :placeholder="saleQuoteText.placeholders.quoteExtras"
          class="app-modal-select sale-quote-extras-select"
        >
          <template #noResult>
            <span>{{ salesModalText.common.noOption }}</span>
          </template>
          <template #noOptions>
            <span>{{ salesModalText.common.noOption }}</span>
          </template>
        </multiselect>
      </div>
    </div>
  </section>
</template>

<script>
import { SALES_TEXT } from '@/texts/catalog/sales';

const SALES_MODAL_TEXT = SALES_TEXT.modal;
const SALE_QUOTE_TEXT = SALES_MODAL_TEXT.content.quote;

export default {
  name: 'SaleModalSectionQuote',
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
  },
  emits: ['change-quote-mode', 'quote-file-change'],
  computed: {
    salesModalText() {
      return SALES_MODAL_TEXT;
    },
    saleQuoteText() {
      return SALE_QUOTE_TEXT;
    },
  },
  methods: {
    openQuoteFilePicker() {
      const input = this.$refs.quoteFileInput;
      if (input && typeof input.click === 'function') {
        input.click();
      }
    },
    onQuoteFileChange(event) {
      const file = event?.target?.files?.[0] || null;
      this.$emit('quote-file-change', file);
    },
  },
};
</script>

<style scoped>
.sale-quote-mode {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #4b5563;
}

.sale-quote-mode label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
  min-height: 40px;
  width: 100%;
  border: 1px solid rgba(153, 152, 153, 0.3);
  border-radius: 8px;
  padding: 0 12px;
  background: #ffffff;
}

.sale-quote-mode label input[type='checkbox'] {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  margin: 0;
  align-self: center;
}

.sale-quote-mode label span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 16px;
  line-height: 16px;
  margin: 0;
  transform: translateY(1px);
}

.sale-file-upload {
  border: 1px solid rgba(153, 152, 153, 0.3);
  border-radius: 8px;
  min-height: 44px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ffffff;
}

.sale-file-upload__input {
  display: none;
}

.sale-file-upload__trigger {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 12px;
  flex-shrink: 0;
}

.sale-file-upload__name {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  font-size: 12px;
  color: #4b5563;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sale-quote-extras-select :deep(.multiselect__tags),
.sale-quote-extras-select :deep(.multiselect-tags) {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  column-gap: 6px;
  row-gap: 6px;
  min-height: 36px !important;
  height: auto !important;
  padding: 10px 36px 10px 8px !important;
  overflow: hidden;
}

.sale-quote-extras-select :deep(.multiselect__tags-wrap) {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  column-gap: 6px;
  row-gap: 6px;
  width: 100%;
  margin: 0;
  overflow: hidden;
}

.sale-quote-extras-select :deep(.multiselect__tag),
.sale-quote-extras-select :deep(.multiselect-tag) {
  background: #e9eff8;
  color: #334155;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  line-height: 1;
  min-height: 26px;
  padding: 0 24px 0 10px;
  margin: 0;
}

.sale-quote-extras-select :deep(.multiselect__tag span),
.sale-quote-extras-select :deep(.multiselect-tag span) {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  line-height: 1;
  margin: 0;
}

.sale-quote-extras-select :deep(.multiselect__tag-icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 26px;
  top: 0;
  margin: 0;
}

.sale-quote-extras-select :deep(.multiselect__tag-icon:after),
.sale-quote-extras-select :deep(.multiselect-tag-remove) {
  color: #64748b;
  line-height: 1;
  margin: 0;
  transform: none;
}

.sale-quote-extras-select :deep(.multiselect__input),
.sale-quote-extras-select :deep(.multiselect-input) {
  margin: 0;
  min-height: 24px;
  line-height: 24px;
}

.sale-quote-extras-select :deep(.multiselect__option--selected),
.sale-quote-extras-select :deep(.multiselect__option--selected.multiselect__option--highlight),
.sale-quote-extras-select :deep(.multiselect-option.is-selected),
.sale-quote-extras-select :deep(.multiselect-option.is-selected.is-pointed) {
  background: #e9eff8;
  color: #334155;
}

.app-modal-select {
  width: 100%;
}
</style>
